(() => {
  'use strict';

  const globalObject = window;
  const documentObject = document;
  if (globalObject.TorrasAnalytics) return;
  const CONFIG_NODE_ID = 'TorrasAnalyticsConfig';
  const REGISTRY_NODE_ID = 'TorrasAnalyticsRegistry';
  const IMPRESSION_SELECTOR = '[data-analytics-impression]';
  const CLICK_SELECTOR = '[data-analytics-click]';
  const INTERACTION_EVENT = 'torras:analytics:interaction';
  const DEFAULT_QUEUE_LIMIT = 50;
  const DEFAULT_RETRY_LIMIT = 8;
  const DEFAULT_RETRY_DELAY = 250;
  const MAX_IDENTIFIER_LENGTH = 100;
  const MAX_PATH_LENGTH = 240;
  const IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;
  const INTERACTION_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
  const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;

  const allowedPageScopes = new Set([
    'account',
    'article',
    'blog',
    'cart',
    'collection',
    'home',
    'other',
    'page',
    'pdp',
    'search',
  ]);
  const allowedElementTypes = new Set([
    'account_action',
    'accordion_item',
    'button',
    'cart_item',
    'category_card',
    'collection_link',
    'component',
    'contact_link',
    'cta',
    'filter',
    'finder_brand',
    'finder_model',
    'legal_link',
    'link',
    'media',
    'menu_item',
    'nav_item',
    'newsletter',
    'product_card',
    'quantity_control',
    'remove_button',
    'search_term',
    'slide',
    'social_link',
    'sort_option',
    'tab',
    'variant_option',
    'video_card',
  ]);
  const allowedDestinationTypes = new Set([
    'account',
    'cart',
    'checkout',
    'collection',
    'external',
    'other',
    'page',
    'policy',
    'product',
    'search',
  ]);
  const allowedInteractionResults = new Set(['cancel', 'error', 'success']);
  const allowedInteractionNames = new Set([
    'add_address',
    'apply_filter',
    'auto_load',
    'cancel',
    'cancel_logout',
    'clear_filter',
    'close',
    'close_menu',
    'collapse',
    'confirm_logout',
    'copy_coupon',
    'delete_address',
    'edit_address',
    'expand',
    'filter_coupons',
    'load_more',
    'mute',
    'open',
    'open_menu',
    'pause',
    'play',
    'remove',
    'request_logout',
    'save_address',
    'save_profile',
    'search_submit',
    'select_brand',
    'select_filter',
    'select_locale',
    'select_model',
    'select_sort',
    'select_tab',
    'select_variant',
    'sign_in',
    'submit',
    'unmute',
    'update_quantity',
    'zoom',
  ]);

  function readJsonNode(id) {
    const node = documentObject.getElementById(id);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch {
      return null;
    }
  }

  const config = readJsonNode(CONFIG_NODE_ID);
  const runtimeRegistry = readJsonNode(REGISTRY_NODE_ID);
  if (!config || !runtimeRegistry || config.schemaVersion !== runtimeRegistry.schemaVersion) return;

  const componentTypes = new Map(Object.entries(runtimeRegistry.components || {}));
  const customEvents = new Set(runtimeRegistry.customEvents || []);
  const eventNamespace = runtimeRegistry.shopifyEventNamespace || 'torras:';
  const exposurePolicy = runtimeRegistry.exposurePolicy || {};
  const minimumVisibleRatio = Number(exposurePolicy.minimumVisibleRatio) || 0.5;
  const minimumVisibleMilliseconds = Number(exposurePolicy.minimumVisibleMilliseconds) || 1000;
  const queueLimit = positiveInteger(config.queueLimit, DEFAULT_QUEUE_LIMIT);
  const retryLimit = positiveInteger(config.retryLimit, DEFAULT_RETRY_LIMIT);
  const retryDelay = positiveInteger(config.retryDelayMilliseconds, DEFAULT_RETRY_DELAY);

  const state = {
    started: false,
    observer: null,
    mutationObserver: null,
    observedElements: new Set(),
    exposureKeys: new Set(),
    viewKeys: new Set(),
    visibility: new WeakMap(),
    queue: [],
    retryCount: 0,
    retryTimer: null,
    diagnostics: {
      dropped: 0,
      published: 0,
      queued: 0,
      rejected: 0,
    },
  };

  function positiveInteger(value, fallback) {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : fallback;
  }

  function debugDiagnostic(code) {
    if (!config.debug || !globalObject.console?.warn) return;
    globalObject.console.warn(`[TorrasAnalytics] ${code}`);
  }

  function reject(code) {
    state.diagnostics.rejected += 1;
    debugDiagnostic(code);
    return null;
  }

  function asIdentifier(value, fieldName) {
    if (value === undefined || value === null || value === '') return null;
    const normalized = String(value).trim().toLowerCase();
    if (
      normalized.length > MAX_IDENTIFIER_LENGTH ||
      !IDENTIFIER_PATTERN.test(normalized) ||
      EMAIL_PATTERN.test(normalized)
    ) {
      reject(`invalid_${fieldName}`);
      return null;
    }
    return normalized;
  }

  function asInteractionName(value) {
    const normalized = asIdentifier(value, 'interaction_name');
    if (!normalized || !INTERACTION_PATTERN.test(normalized) || !allowedInteractionNames.has(normalized)) {
      if (normalized) reject('unknown_interaction_name');
      return null;
    }
    return normalized;
  }

  function asPositivePosition(value) {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    if (!Number.isInteger(number) || number < 1 || number > 10000) {
      reject('invalid_element_position');
      return null;
    }
    return number;
  }

  function sanitizeDestinationPath(value) {
    if (!value) return null;
    try {
      const url = new URL(String(value), globalObject.location.origin);
      if (url.origin !== globalObject.location.origin) return null;
      if (!url.pathname.startsWith('/') || url.pathname.length > MAX_PATH_LENGTH) return null;
      return url.pathname;
    } catch {
      return null;
    }
  }

  function closestElement(target, selector) {
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest(selector);
  }

  function datasetValue(element, name) {
    return element?.dataset?.[name];
  }

  function detailValue(details, snakeName, camelName) {
    return details?.[snakeName] ?? details?.[camelName];
  }

  function resolveComponent(target, details) {
    const componentRoot = closestElement(target, '[data-analytics-component-id]');
    const requestedId =
      detailValue(details, 'component_id', 'componentId') ||
      datasetValue(componentRoot, 'analyticsComponentId');
    const componentId = asIdentifier(requestedId, 'component_id');
    if (!componentId || !componentTypes.has(componentId)) return reject('unregistered_component_id');

    const registeredType = componentTypes.get(componentId);
    const declaredType =
      detailValue(details, 'component_type', 'componentType') ||
      datasetValue(componentRoot, 'analyticsComponentType');
    if (declaredType && declaredType !== registeredType) return reject('component_type_mismatch');
    return { componentId, componentRoot, componentType: registeredType };
  }

  function buildPayload(eventName, target, details = {}) {
    if (!customEvents.has(eventName)) return reject('unknown_event_name');
    if (!allowedPageScopes.has(config.pageScope)) return reject('invalid_page_scope');

    const pageKey = asIdentifier(config.pageKey, 'page_key');
    if (!pageKey) return null;
    const component = resolveComponent(target, details);
    if (!component) return null;

    const elementType = asIdentifier(
      detailValue(details, 'element_type', 'elementType') || datasetValue(target, 'analyticsElementType'),
      'element_type',
    );
    const elementId = asIdentifier(
      detailValue(details, 'element_id', 'elementId') || datasetValue(target, 'analyticsElementId'),
      'element_id',
    );
    if (!elementType || !allowedElementTypes.has(elementType)) return reject('unknown_element_type');
    if (!elementId) return null;

    const payload = {
      schema_version: String(config.schemaVersion),
      page_scope: config.pageScope,
      page_key: pageKey,
      component_type: component.componentType,
      component_id: component.componentId,
      element_type: elementType,
      element_id: elementId,
    };

    const position = asPositivePosition(
      detailValue(details, 'element_position', 'elementPosition') ||
        datasetValue(target, 'analyticsElementPosition'),
    );
    if (position) payload.element_position = position;

    const destinationType = asIdentifier(
      detailValue(details, 'destination_type', 'destinationType') ||
        datasetValue(target, 'analyticsDestinationType'),
      'destination_type',
    );
    if (destinationType) {
      if (!allowedDestinationTypes.has(destinationType)) return reject('unknown_destination_type');
      payload.destination_type = destinationType;
    }

    const destinationPathValue =
      detailValue(details, 'destination_path', 'destinationPath') ||
      datasetValue(target, 'analyticsDestinationPath');
    if (destinationPathValue) {
      const destinationPath = sanitizeDestinationPath(destinationPathValue);
      if (!destinationPath) return reject('invalid_destination_path');
      payload.destination_path = destinationPath;
    }

    if (eventName === 'ui_component_interaction') {
      const interactionName = asInteractionName(
        detailValue(details, 'interaction_name', 'interactionName') ||
          datasetValue(target, 'analyticsInteractionName'),
      );
      if (!interactionName) return null;
      payload.interaction_name = interactionName;

      const interactionResult = asIdentifier(
        detailValue(details, 'interaction_result', 'interactionResult') ||
          datasetValue(target, 'analyticsInteractionResult'),
        'interaction_result',
      );
      if (interactionResult) {
        if (!allowedInteractionResults.has(interactionResult)) {
          return reject('unknown_interaction_result');
        }
        payload.interaction_result = interactionResult;
      }
    }

    return payload;
  }

  function publisher() {
    const publish = globalObject.Shopify?.analytics?.publish;
    return typeof publish === 'function' ? publish.bind(globalObject.Shopify.analytics) : null;
  }

  function tryPublish(item) {
    const publish = publisher();
    if (!publish) return false;
    try {
      const result = publish(`${eventNamespace}${item.eventName}`, item.payload);
      if (result && typeof result.catch === 'function') {
        result.catch(() => {
          state.diagnostics.dropped += 1;
          debugDiagnostic('async_publish_failed');
        });
      }
      state.diagnostics.published += 1;
      return true;
    } catch {
      debugDiagnostic('publish_failed');
      return false;
    }
  }

  function clearRetryTimer() {
    if (!state.retryTimer) return;
    globalObject.clearTimeout(state.retryTimer);
    state.retryTimer = null;
  }

  function scheduleFlush() {
    if (state.retryTimer || state.queue.length === 0) return;
    if (state.retryCount >= retryLimit) {
      state.diagnostics.dropped += state.queue.length;
      state.queue.length = 0;
      state.diagnostics.queued = 0;
      debugDiagnostic('queue_retry_limit');
      return;
    }
    state.retryTimer = globalObject.setTimeout(() => {
      state.retryTimer = null;
      flushQueue();
    }, retryDelay);
  }

  function flushQueue() {
    if (state.queue.length === 0) {
      clearRetryTimer();
      return;
    }
    if (!publisher()) {
      state.retryCount += 1;
      scheduleFlush();
      return;
    }

    clearRetryTimer();
    const pending = state.queue.splice(0);
    state.diagnostics.queued = 0;
    state.retryCount = 0;
    for (const item of pending) {
      if (!tryPublish(item)) {
        state.diagnostics.dropped += 1;
      }
    }
  }

  function publish(eventName, payload) {
    if (!payload) return false;
    const item = { eventName, payload };
    if (tryPublish(item)) {
      flushQueue();
      return true;
    }

    if (state.queue.length >= queueLimit) {
      state.queue.shift();
      state.diagnostics.dropped += 1;
    }
    state.queue.push(item);
    state.diagnostics.queued = state.queue.length;
    scheduleFlush();
    return false;
  }

  function exposureKey(payload) {
    return [payload.component_id, payload.element_type, payload.element_id].join('|');
  }

  function isEligibleForExposure(element) {
    if (!element || element.isConnected === false) return false;
    if (documentObject.visibilityState === 'hidden') return false;
    if (closestElement(element, '[hidden], [aria-hidden="true"], [inert]')) return false;
    if (typeof globalObject.getComputedStyle === 'function') {
      for (let node = element; node; node = node.parentElement) {
        const style = globalObject.getComputedStyle(node);
        if (
          style?.display === 'none' ||
          style?.visibility === 'hidden' ||
          style?.visibility === 'collapse' ||
          Number(style?.opacity) === 0
        ) {
          return false;
        }
      }
    }
    return true;
  }

  function publishView(payload) {
    const key = [exposureKey(payload), payload.element_position || '', payload.destination_type || ''].join('|');
    if (state.viewKeys.has(key)) return;
    state.viewKeys.add(key);
    publish('ui_component_view', { ...payload, schema_version: '2' });
  }

  function clearVisibilityTimer(element) {
    const visibility = state.visibility.get(element);
    if (!visibility?.timer) return;
    globalObject.clearTimeout(visibility.timer);
    visibility.timer = null;
  }

  function completeExposure(element) {
    const visibility = state.visibility.get(element);
    if (
      !visibility ||
      visibility.ratio < minimumVisibleRatio ||
      !isEligibleForExposure(element)
    ) {
      clearVisibilityTimer(element);
      return;
    }
    const payload = buildPayload('ui_component_impression', element);
    if (!payload) {
      unobserveElement(element);
      return;
    }
    const key = exposureKey(payload);
    if (!state.exposureKeys.has(key)) {
      state.exposureKeys.add(key);
      publish('ui_component_impression', payload);
    }
    unobserveElement(element);
  }

  function handleIntersectionEntries(entries) {
    for (const entry of entries) {
      const element = entry.target;
      const ratio = Number(entry.intersectionRatio) || 0;
      const visibility = state.visibility.get(element) || { ratio: 0, timer: null };
      visibility.ratio = ratio;
      visibility.eligible = isEligibleForExposure(element);
      state.visibility.set(element, visibility);

      if (entry.isIntersecting && ratio >= minimumVisibleRatio && visibility.eligible) {
        const payload = buildPayload('ui_component_view', element);
        if (payload) publishView(payload);
        if (!visibility.timer) {
          visibility.timer = globalObject.setTimeout(
            () => completeExposure(element),
            minimumVisibleMilliseconds,
          );
        }
      } else {
        clearVisibilityTimer(element);
      }
    }
  }

  function observeElement(element) {
    if (
      !state.observer ||
      !element ||
      element.isConnected === false ||
      state.observedElements.has(element)
    ) {
      return;
    }
    const payload = buildPayload('ui_component_impression', element);
    if (!payload || state.exposureKeys.has(exposureKey(payload))) return;
    state.observedElements.add(element);
    state.visibility.set(element, { ratio: 0, timer: null, eligible: isEligibleForExposure(element) });
    state.observer.observe(element);
  }

  function unobserveElement(element) {
    if (!element || !state.observedElements.has(element)) return;
    clearVisibilityTimer(element);
    state.observer?.unobserve(element);
    state.observedElements.delete(element);
    state.visibility.delete(element);
  }

  function impressionElements(root) {
    const elements = [];
    if (root?.matches?.(IMPRESSION_SELECTOR)) elements.push(root);
    if (typeof root?.querySelectorAll === 'function') {
      elements.push(...root.querySelectorAll(IMPRESSION_SELECTOR));
    }
    return elements;
  }

  function registerTree(root = documentObject) {
    for (const element of impressionElements(root)) observeElement(element);
  }

  function unregisterTree(root) {
    for (const element of impressionElements(root)) unobserveElement(element);
  }

  function refreshTree(root) {
    for (const element of impressionElements(root)) {
      const visibility = state.visibility.get(element);
      const eligible = isEligibleForExposure(element);
      if (!visibility) {
        observeElement(element);
      } else if (visibility.eligible !== eligible) {
        // Only a real visibility transition resets continuous exposure time.
        unobserveElement(element);
        observeElement(element);
      }
    }
  }

  function handleMutations(mutations) {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.removedNodes || []) unregisterTree(node);
        for (const node of mutation.addedNodes || []) registerTree(node);
      } else if (mutation.type === 'attributes') {
        refreshTree(mutation.target);
      }
    }
  }

  function trackClick(target, details = {}) {
    const payload = buildPayload('ui_component_click', target, details);
    if (!payload || !isEligibleForExposure(target)) return false;
    payload.schema_version = '2';
    // A real interaction proves an opportunity to see the element even when
    // navigation occurs before the one-second engaged-impression threshold.
    publishView(payload);
    return publish('ui_component_click', payload);
  }

  function trackInteraction(target, details = {}) {
    return publish(
      'ui_component_interaction',
      buildPayload('ui_component_interaction', target, details),
    );
  }

  function trackImpression(target, details = {}) {
    const payload = buildPayload('ui_component_impression', target, details);
    if (!payload || state.exposureKeys.has(exposureKey(payload))) return false;
    observeElement(target);
    return state.observedElements.has(target);
  }

  function handleClick(event) {
    if (event.isTrusted === false) return;
    const target = closestElement(event.target, CLICK_SELECTOR);
    if (!target) return;
    trackClick(target);
  }

  function handleInteractionEvent(event) {
    trackInteraction(event.target, event.detail || {});
  }

  function start() {
    if (state.started) return;
    state.started = true;

    if (typeof globalObject.IntersectionObserver === 'function') {
      state.observer = new globalObject.IntersectionObserver(handleIntersectionEntries, {
        threshold: [0, minimumVisibleRatio, 1],
      });
      registerTree(documentObject);
    } else {
      debugDiagnostic('intersection_observer_unavailable');
    }

    if (typeof globalObject.MutationObserver === 'function' && documentObject.body) {
      state.mutationObserver = new globalObject.MutationObserver(handleMutations);
      state.mutationObserver.observe(documentObject.body, {
        attributeFilter: ['aria-hidden', 'class', 'hidden', 'inert', 'open', 'style'],
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    documentObject.addEventListener('click', handleClick, true);
    documentObject.addEventListener('visibilitychange', () => {
      for (const element of [...state.observedElements]) {
        unobserveElement(element);
        observeElement(element);
      }
    });
    documentObject.addEventListener(INTERACTION_EVENT, handleInteractionEvent);
    documentObject.addEventListener('shopify:section:load', (event) => registerTree(event.target));
    documentObject.addEventListener('shopify:section:unload', (event) => unregisterTree(event.target));
    flushQueue();
  }

  function getDebugState() {
    return {
      diagnostics: { ...state.diagnostics },
      exposureCount: state.exposureKeys.size,
      viewCount: state.viewKeys.size,
      observedCount: state.observedElements.size,
      queueLength: state.queue.length,
      started: state.started,
    };
  }

  const api = Object.freeze({
    eventName: INTERACTION_EVENT,
    getDebugState,
    registerTree,
    trackClick,
    trackImpression,
    trackInteraction,
  });
  globalObject.TorrasAnalytics = api;

  if (config.debug) {
    globalObject.__TorrasAnalyticsTest = Object.freeze({
      buildPayload,
      flushQueue,
      handleIntersectionEntries,
      handleMutations,
      isEligibleForExposure,
      sanitizeDestinationPath,
      state,
    });
  }

  if (documentObject.readyState === 'loading') {
    documentObject.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
